import type { Kommentar } from "../../notizbloecke/kommentarModell";

/**
 * WELCHE Kommentare die Spalte neben einer Verlaufskarte zeigt — REINE
 * Ableitung, bewusst ohne React, damit der node-Runner sie direkt prüft.
 *
 * ══ SICHTBAR IST EIN KOMMENTAR ═════════════════════════════════════════════
 * „Um die Anzeige nicht zu überladen … eine Lösung, um bei der Spalte
 * Kommentare nur einen Kommentar anzuzeigen" (PO 18.09.2026). Drei Rückfragen
 * an einer Mail machten die Spalte sonst länger als die Mail daneben, und die
 * Liste der Karten zerfiele in Absätze verschiedener Höhe.
 *
 * Der Rest steht hinter „n weitere" und ist EINEN Klick entfernt — nichts
 * verschwindet, es steht nur nicht alles gleichzeitig da.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Welcher Kommentar steht in der zugeklappten Spalte? Der jüngste OFFENE —
 * und wenn alles abgehakt ist, der jüngste überhaupt.
 *
 * Er ist der, an dem noch etwas hängt: Wer die Karte überfliegt, will wissen,
 * was hier noch zu tun ist. Ein abgehakter Kommentar ist erledigt; ihn
 * voranzustellen, während darunter eine offene Bitte liegt, kehrte die Frage
 * um.
 *
 * Ist ALLES abgehakt, bleibt trotzdem einer stehen: Eine leere Spalte an einer
 * Karte, unter der drei erledigte Rückfragen liegen, sähe aus wie „hier ist
 * nichts passiert".
 *
 * `kommentareJeZiel` liefert die Liste ÄLTESTE ZUERST (die Leserichtung eines
 * Gesprächs) — der jüngste ist damit der letzte.
 *
 * -1 bei leerer Liste; der Aufrufer zeichnet dann gar keinen Kommentar.
 */
export function sichtbarerIndex(liste: Kommentar[]): number {
    for (let i = liste.length - 1; i >= 0; i--) {
        if (!liste[i]!.erledigtAm) return i;
    }
    return liste.length - 1;
}

/** Wie viele der VERSTECKTEN noch offen sind — „2 weitere · 1 offen". */
export function offeneVersteckte(liste: Kommentar[], sichtbar: number): number {
    return liste.filter((k, i) => i !== sichtbar && !k.erledigtAm).length;
}

export type SpaltenAnsicht = {
    /** Was gezeichnet wird — aufgeklappt älteste zuerst, sonst der eine. */
    zeigen: Kommentar[];
    /** Wie viele nicht gezeichnet werden; 0 = es steht alles da. */
    versteckt: number;
    /**
     * Wie viele der versteckten noch OFFEN sind. Ohne die Zahl sähe „2
     * weitere" aus wie Beiwerk — mit ihr sagt der Knopf, dass sich das
     * Aufklappen lohnt.
     */
    offenVersteckt: number;
};

/**
 * Die Spalte für einen Faden. `auf` ist der gemerkte Zustand der Karte
 * (siehe `kommentarAufgeklappt.ts`).
 *
 * Bei EINEM Kommentar gibt es nichts zu klappen — dann ist „zugeklappt" und
 * „aufgeklappt" dasselbe Bild, und der Knopf „1 weiterer" wäre gelogen.
 */
export function spaltenAnsicht(liste: Kommentar[], auf: boolean): SpaltenAnsicht {
    if (auf || liste.length <= 1) {
        return { zeigen: liste, versteckt: 0, offenVersteckt: 0 };
    }
    const i = sichtbarerIndex(liste);
    const eine = liste[i];
    if (!eine) return { zeigen: [], versteckt: 0, offenVersteckt: 0 };
    return {
        zeigen: [eine],
        versteckt: liste.length - 1,
        offenVersteckt: offeneVersteckte(liste, i),
    };
}

/** „1 weiterer" / „2 weitere" — die Beschriftung des Aufklappers. */
export function mehrLabel(versteckt: number): string {
    return `${versteckt} ${versteckt === 1 ? "weiterer" : "weitere"}`;
}
